import { 
  collection, 
  doc, 
  setDoc, 
  deleteDoc, 
  getDoc,
  getDocs,
  query, 
  where, 
  orderBy,
  onSnapshot,
  serverTimestamp,
  limit,
  startAfter,
  QuerySnapshot,
  DocumentData,
  Unsubscribe
} from 'firebase/firestore'
import { db } from '../../lib/firebase'

export interface Bookmark {
  id: string
  userId: string
  routeId: string
  routeName: string
  routeThumbnail?: string
  routeDistance?: number
  routeElevation?: number
  routeLocation?: string
  routeType?: string
  clubId?: string
  clubName?: string
  createdAt: any
}

export interface BookmarkInput {
  routeId: string
  routeName: string
  routeThumbnail?: string
  routeDistance?: number
  routeElevation?: number
  routeLocation?: string
  routeType?: string
  clubId?: string
  clubName?: string
}

// Add or remove a bookmark
export async function toggleBookmark(userId: string, bookmarkData: BookmarkInput): Promise<boolean> {
  if (!userId || !bookmarkData.routeId) {
    throw new Error('User ID and route ID are required')
  }

  try {
    // Check if bookmark exists
    const isBookmarked = await checkBookmarkExists(userId, bookmarkData.routeId)
    
    if (isBookmarked) {
      // Remove bookmark
      await removeBookmark(userId, bookmarkData.routeId)
      return false
    } else {
      // Add bookmark
      await addBookmark(userId, bookmarkData)
      return true
    }
  } catch (error) {
    console.error('Error toggling bookmark:', error)
    throw error
  }
}

// Add a bookmark
async function addBookmark(userId: string, bookmarkData: BookmarkInput): Promise<void> {
  const bookmarkId = `${userId}_${bookmarkData.routeId}`
  
  // Build bookmark object without undefined values
  const bookmark: any = {
    userId,
    routeId: bookmarkData.routeId,
    routeName: bookmarkData.routeName,
    createdAt: serverTimestamp()
  }
  
  // Only add optional fields if they have values
  if (bookmarkData.routeThumbnail !== undefined) {
    bookmark.routeThumbnail = bookmarkData.routeThumbnail
  }
  if (bookmarkData.routeDistance !== undefined) {
    bookmark.routeDistance = bookmarkData.routeDistance
  }
  if (bookmarkData.routeElevation !== undefined) {
    bookmark.routeElevation = bookmarkData.routeElevation
  }
  if (bookmarkData.routeLocation !== undefined) {
    bookmark.routeLocation = bookmarkData.routeLocation
  }
  if (bookmarkData.routeType !== undefined) {
    bookmark.routeType = bookmarkData.routeType
  }
  if (bookmarkData.clubId !== undefined) {
    bookmark.clubId = bookmarkData.clubId
  }
  if (bookmarkData.clubName !== undefined) {
    bookmark.clubName = bookmarkData.clubName
  }
  
  await setDoc(doc(db, 'users', userId, 'bookmarks', bookmarkId), bookmark)
}

// Remove a bookmark
async function removeBookmark(userId: string, routeId: string): Promise<void> {
  const bookmarkId = `${userId}_${routeId}`
  await deleteDoc(doc(db, 'users', userId, 'bookmarks', bookmarkId))
}

// Check if a route is bookmarked by a user
export async function checkBookmarkExists(userId: string, routeId: string): Promise<boolean> {
  if (!userId || !routeId) return false
  
  const bookmarkId = `${userId}_${routeId}`
  const bookmarkDoc = await getDoc(doc(db, 'users', userId, 'bookmarks', bookmarkId))
  return bookmarkDoc.exists()
}

// Get all bookmarks for a user
export async function getUserBookmarks(
  userId: string, 
  pageSize: number = 20, 
  lastDoc?: DocumentData
): Promise<{ bookmarks: Bookmark[], lastDoc: DocumentData | null }> {
  if (!userId) {
    return { bookmarks: [], lastDoc: null }
  }

  let q = query(
    collection(db, 'users', userId, 'bookmarks'),
    orderBy('createdAt', 'desc'),
    limit(pageSize)
  )

  if (lastDoc) {
    q = query(q, startAfter(lastDoc))
  }

  const snapshot = await getDocs(q)
  const bookmarks: Bookmark[] = []
  
  snapshot.forEach((doc) => {
    bookmarks.push({
      id: doc.id,
      ...doc.data()
    } as Bookmark)
  })

  const newLastDoc = snapshot.docs[snapshot.docs.length - 1] || null
  
  return { bookmarks, lastDoc: newLastDoc }
}

// Subscribe to bookmark changes for a user
export function subscribeToUserBookmarks(
  userId: string,
  callback: (bookmarks: Bookmark[]) => void
): Unsubscribe {
  if (!userId) {
    callback([])
    return () => {}
  }

  const q = query(
    collection(db, 'users', userId, 'bookmarks'),
    orderBy('createdAt', 'desc')
  )

  return onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
    const bookmarks: Bookmark[] = []
    snapshot.forEach((doc) => {
      bookmarks.push({
        id: doc.id,
        ...doc.data()
      } as Bookmark)
    })
    callback(bookmarks)
  })
}

// Check multiple routes for bookmark status (batch operation)
export async function checkMultipleBookmarks(
  userId: string, 
  routeIds: string[]
): Promise<Set<string>> {
  if (!userId || routeIds.length === 0) {
    return new Set()
  }

  const bookmarkedRoutes = new Set<string>()
  
  // Check in batches of 10 to avoid Firestore limitations
  const batchSize = 10
  for (let i = 0; i < routeIds.length; i += batchSize) {
    const batch = routeIds.slice(i, i + batchSize)
    const bookmarkIds = batch.map(routeId => `${userId}_${routeId}`)
    
    // Check each bookmark document
    const promises = bookmarkIds.map(async (bookmarkId, index) => {
      const bookmarkDoc = await getDoc(doc(db, 'users', userId, 'bookmarks', bookmarkId))
      if (bookmarkDoc.exists()) {
        bookmarkedRoutes.add(batch[index])
      }
    })
    
    await Promise.all(promises)
  }
  
  return bookmarkedRoutes
}

// Get bookmark count for a route (for analytics)
// NOTE: This function is not efficient with subcollection structure
// Consider maintaining a counter on the route document itself
export async function getRouteBookmarkCount(routeId: string): Promise<number> {
  // This would require querying all users' bookmarks subcollections
  // which is not feasible. Instead, consider:
  // 1. Maintaining a bookmark counter on the route document
  // 2. Using a separate bookmarks_analytics collection
  console.warn('getRouteBookmarkCount is not implemented for subcollection structure')
  return 0
}